import type { AnswerEvent, Atlas } from '../lib/cognitiveEngine';
import { REGIONS } from '../lib/cognitiveEngine';
import {
  RANK_TIERS, computeMatchDelta, fullRegions, nextTierOf, ratingTimeline, tierOf,
} from '../lib/atlasRank';

/**
 * Atlas Rank, rendered. Three cards: the rank card (name, number, next rung,
 * five badges), the rating graph, and the match delta. All numbers come from
 * lib/atlasRank, which reads the engine; these components only draw.
 *
 * Every card is width-flexible with a 360px design width: that is a real phone
 * width, so what the lab shows is what the phone gets.
 */

const CARD: React.CSSProperties = {
  background: 'linear-gradient(140deg, rgba(20,184,166,0.08) 0%, transparent 55%), #182541',
  border: '1px solid rgba(255,255,255,0.14)',
  borderRadius: 14,
  padding: 18,
  width: '100%',
  maxWidth: 380,
  boxSizing: 'border-box',
};

const LABEL: React.CSSProperties = {
  fontFamily: 'var(--font-sans)',
  fontWeight: 700,
  fontSize: '0.85rem',
  letterSpacing: '0.12em',
  color: '#C4C4D2',
};

/**
 * Region badge: hexagon, monogram, tier ring. Locked until 10 answers, and a
 * locked badge is a filling dial, not a closed box: the outline itself charges
 * up one tenth per answer, so the very first game visibly moves something.
 * Under each badge, the realms that feed it: the player is not expected to
 * guess that ENIGMA counts toward Abstract.
 */
function RegionBadge({ letter, label, elo, n, locked, realms }: {
  letter: string; label: string; elo: number; n: number; locked: boolean; realms: string[];
}) {
  const tier = tierOf(elo);
  const hex = Array.from({ length: 6 }, (_, i) => {
    const a = ((i * 60 - 90) * Math.PI) / 180;
    return `${(32 + 28 * Math.cos(a)).toFixed(1)},${(32 + 28 * Math.sin(a)).toFixed(1)}`;
  }).join(' ');
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 10, width: '100%',
      padding: '5px 10px', borderRadius: 12,
      background: locked ? 'rgba(255,255,255,0.04)' : `${tier.color}12`,
      border: `1px solid ${locked ? 'rgba(255,255,255,0.08)' : `${tier.color}44`}`,
    }}>
      <svg
        viewBox="0 0 64 64" width={38} height={38}
        style={{ display: 'block', flexShrink: 0, filter: !locked && tier.glow ? `drop-shadow(0 0 5px ${tier.color}66)` : undefined }}
      >
        <polygon points={hex} fill={locked ? '#182338' : '#0E1626'}
          stroke={locked ? '#33415E' : tier.color} strokeWidth={locked ? 1.5 : 2} strokeLinejoin="round" />
        {locked && n > 0 && (
          <polygon points={hex} pathLength={100} fill="none" stroke="#3E7BFA" strokeWidth={1.5}
            strokeDasharray={`${Math.min(100, n * 10)} 100`} strokeLinejoin="round" strokeLinecap="round" />
        )}
        <text x="32" y="34" textAnchor="middle" dominantBaseline="middle"
          fontFamily="var(--font-heading), sans-serif" fontStyle="italic" fontWeight="900"
          fontSize="22" fill={locked ? '#55506E' : tier.color}>
          {letter}
        </text>
      </svg>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0, flex: 1 }}>
        <span style={{ fontWeight: 800, fontSize: '0.95rem', color: locked ? '#B8B8CA' : '#fff', letterSpacing: '0.02em' }}>
          {label}
        </span>
        <span style={{ fontSize: '0.8rem', color: '#8E8EA2', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {realms.map(r => r.charAt(0) + r.slice(1).toLowerCase()).join(', ')}
        </span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2, flexShrink: 0 }}>
        {locked ? (
          <>
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: '0.95rem', fontWeight: 700, color: '#C4C4D2' }}>
              {n}/10
            </span>
            <span style={{ fontSize: '0.75rem', fontWeight: 600, color: '#8E8EA2' }}>to unlock</span>
          </>
        ) : (
          <>
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: '1.05rem', fontWeight: 800, color: '#fff' }}>
              {Math.round(elo)}
            </span>
            <span style={{ fontSize: '0.78rem', fontWeight: 700, color: tier.color, whiteSpace: 'nowrap' }}>{tier.name}</span>
          </>
        )}
      </div>
    </div>
  );
}

function BadgeRow({ atlas }: { atlas: Atlas }) {
  // Rows, not five 60px columns: HUMANITIES never fit under a hex on a
  // 375px screen and the labels collided into one unreadable strip.
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {fullRegions(atlas).map(r => (
        <RegionBadge
          key={r.region}
          letter={r.name[0]}
          label={r.name}
          elo={r.elo}
          n={r.n}
          locked={r.tier === 'HIDDEN'}
          realms={REGIONS[r.region]?.realms ?? []}
        />
      ))}
    </div>
  );
}

/**
 * The rank card: one name, one number, one next rung, five badges. UNRANKED
 * shares the exact anatomy (name, bar, next step) so the empty state leads
 * with the goal, not the void.
 */
export function RankCard({ atlas, label = 'ATLAS RANK' }: { atlas: Atlas; label?: string }) {
  const elo = atlas.globalElo;
  const ranked = elo !== null;
  const tier = ranked ? tierOf(elo) : null;
  const next = ranked ? nextTierOf(elo) : null;
  const floor = ranked ? (tier!.min === -Infinity ? 940 : tier!.min) : 0;
  const frac = ranked && next ? Math.max(0.03, Math.min(1, (elo - floor) / (next.min - floor))) : 1;
  const closest = [...fullRegions(atlas)].sort((a, b) => b.n - a.n)[0];
  return (
    <div style={CARD}>
      <div style={LABEL}>{label}</div>
      {ranked ? (
        <>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginTop: 10 }}>
            <span style={{ fontFamily: 'var(--font-heading)', fontStyle: 'italic', fontWeight: 900, fontSize: '1.7rem', color: tier!.color, lineHeight: 1 }}>
              {tier!.name}
            </span>
            <span style={{ fontSize: '1.05rem', fontWeight: 700, color: '#e6e6e6', fontVariantNumeric: 'tabular-nums' }}>
              {elo}
            </span>
          </div>
          <div style={{ height: 5, background: '#2C3A58', borderRadius: 3, marginTop: 12 }}>
            <div style={{ width: `${frac * 100}%`, height: '100%', borderRadius: 3, background: tier!.color }} />
          </div>
          <div style={{ fontSize: '0.8rem', color: '#b6b6be', marginTop: 8 }}>
            {next
              ? <>Next: <strong style={{ color: '#e6e6e6' }}>{next.name}</strong> · {next.min - elo} points to climb</>
              : <>Top of the ladder. Defend it.</>}
          </div>
        </>
      ) : (
        <>
          <div style={{ fontFamily: 'var(--font-heading)', fontStyle: 'italic', fontWeight: 900, fontSize: '1.7rem', color: '#b6b6be', marginTop: 10, lineHeight: 1 }}>
            UNRANKED
          </div>
          <div style={{ height: 5, background: '#2C3A58', borderRadius: 3, marginTop: 12 }}>
            <div style={{
              width: `${Math.max(3, (closest?.n ?? 0) * 10)}%`, height: '100%',
              borderRadius: 3, background: '#3E7BFA',
            }} />
          </div>
          <div style={{ fontSize: '0.8rem', color: '#b6b6be', marginTop: 8 }}>
            Placement: <strong style={{ color: '#e6e6e6' }}>{closest?.name ?? 'any region'}</strong>
            {' '}{closest?.n ?? 0} / 10 answers
          </div>
        </>
      )}
      <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid #2C3A58' }}>
        <BadgeRow atlas={atlas} />
      </div>
    </div>
  );
}

export function RatingGraph({ events }: { events: AnswerEvent[] }) {
  const line = ratingTimeline(events);
  if (line.length < 2) return null;
  const W = 324, H = 150, PX = 6;
  const lo = Math.min(...line) - 15;
  const hi = Math.max(...line) + 15;
  const x = (i: number) => PX + (i / (line.length - 1)) * (W - 2 * PX);
  const y = (v: number) => H - PX - ((v - lo) / (hi - lo)) * (H - 2 * PX);
  const pts = line.map((v, i) => `${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(' ');
  const last = line[line.length - 1];
  const tier = tierOf(last);
  return (
    <div style={CARD}>
      <div style={{ ...LABEL, marginBottom: 8 }}>RATING · PER MATCH</div>
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" style={{ display: 'block' }}>
        {RANK_TIERS.filter(t => t.min > lo && t.min < hi).map(t => (
          <g key={t.name}>
            <line x1={PX} x2={W - PX} y1={y(t.min)} y2={y(t.min)} stroke="#2C3A58" strokeWidth="1" />
            <text x={W - PX} y={y(t.min) - 4} textAnchor="end" fontFamily="var(--font-mono)" fontSize="8" fill="#b6b6be" letterSpacing="1.5">
              {t.name}
            </text>
          </g>
        ))}
        <polyline points={pts} fill="none" stroke="#3E7BFA" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
        <circle cx={x(line.length - 1)} cy={y(last)} r="3.5" fill={tier.color} />
      </svg>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', color: '#b6b6be', marginTop: 6 }}>
        <span>{line.length} matches</span>
        <span style={{ color: '#e6e6e6', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{line[0]} → {last}</span>
      </div>
    </div>
  );
}

/**
 * The moment after a match: what this match did to the numbers. The biggest
 * move is the hero, the rest are listed underneath instead of being hidden.
 */
export function MatchDeltaCard({ events, lastN }: { events: AnswerEvent[]; lastN: number }) {
  const delta = computeMatchDelta(events, lastN);
  if (!delta) return null;
  // Uniform game rows: a filled delta pill, the region, and a movement bar.
  // No arrows, no explainer paragraph; the colors and the bar ARE the rule.
  const maxAbs = Math.max(...delta.changes.map(c => Math.abs(c.after - c.before)), 1);
  return (
    <div style={CARD}>
      <div style={LABEL}>THIS MATCH</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 9, marginTop: 12 }}>
        {delta.changes.map((c, i) => {
          const cd = c.after - c.before;
          const up = cd >= 0;
          const hue = up ? '#2DD4BF' : '#FB7185';
          return (
            <div key={c.region} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{
                fontFamily: 'var(--font-mono)', fontWeight: 800, fontVariantNumeric: 'tabular-nums',
                fontSize: i === 0 ? '1rem' : '0.85rem',
                minWidth: i === 0 ? 56 : 48, textAlign: 'center',
                padding: i === 0 ? '6px 0' : '4px 0', borderRadius: 999,
                background: `${hue}${up ? '26' : '22'}`, border: `1px solid ${hue}66`,
                color: hue, flexShrink: 0,
              }}>
                {up ? `+${cd}` : cd}
              </span>
              <span style={{
                fontSize: i === 0 ? '1rem' : '0.9rem', fontWeight: i === 0 ? 800 : 600,
                color: i === 0 ? '#fff' : '#C8C8D6', flexShrink: 0,
              }}>{c.name}</span>
              <span style={{ flex: 1, height: 6, borderRadius: 3, background: 'rgba(255,255,255,0.08)', overflow: 'hidden', display: 'flex', justifyContent: up ? 'flex-start' : 'flex-end' }}>
                <span style={{
                  width: `${Math.max(10, Math.abs(cd) / maxAbs * 100)}%`, height: '100%',
                  background: hue, borderRadius: 3, opacity: 0.85,
                }} />
              </span>
              <span style={{
                fontFamily: 'var(--font-mono)', fontSize: '0.85rem', fontWeight: 700,
                color: '#ECECF2', fontVariantNumeric: 'tabular-nums', flexShrink: 0,
              }}>{c.after}</span>
            </div>
          );
        })}
      </div>
      {delta.rating !== null && (
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          marginTop: 14, paddingTop: 12, borderTop: '1px solid rgba(255,255,255,0.1)',
        }}>
          <span style={{ fontSize: '0.85rem', fontWeight: 700, color: '#C4C4D2' }}>
            ATLAS <span style={{ fontFamily: 'var(--font-mono)', color: '#fff' }}>{delta.rating}</span>
          </span>
          {delta.next ? (
            <span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#7EA8FF' }}>
              {delta.next.min - delta.rating} pts to {delta.next.name}
            </span>
          ) : (
            <span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#2DD4BF' }}>top of the ladder</span>
          )}
        </div>
      )}
      {delta.rating === null && delta.placement && (
        // Before the first rank the number is a countdown, not a score: the
        // region nearest to unlocking, and how many answers are left.
        <div style={{ marginTop: 14, paddingTop: 12, borderTop: '1px solid rgba(255,255,255,0.1)' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={{ fontSize: '0.85rem', fontWeight: 700, color: '#C4C4D2' }}>
              ATLAS <span style={{ color: '#8E8EA2', fontWeight: 600 }}>unranked</span>
            </span>
            <span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#7EA8FF' }}>
              {delta.placement.need - delta.placement.n} more {delta.placement.name} answers to rank
            </span>
          </div>
          <div style={{ height: 5, background: 'rgba(255,255,255,0.08)', borderRadius: 3, marginTop: 8 }}>
            <div style={{ width: `${Math.max(4, delta.placement.n / delta.placement.need * 100)}%`, height: '100%', borderRadius: 3, background: '#3E7BFA' }} />
          </div>
        </div>
      )}
    </div>
  );
}
